import { useState, useEffect, useCallback } from 'react';
import { getNextQuiz } from '../../services/quizService';

/**
 * Custom hook to fetch and manage the next quiz in the learning progression.
 *
 * @param {string|null} topicId - UUID of the topic to get the next quiz for.
 * @returns {{
 *   nextQuiz: object|null,
 *   hasNext: boolean,
 *   loading: boolean,
 *   error: any,
 *   refetch: () => Promise<void>
 * }}
 */
export default function useNextQuiz(topicId) {
  const [nextQuiz, setNextQuiz] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNextQuiz = useCallback(async () => {
    if (!topicId) {
      setNextQuiz(null);
      setHasNext(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getNextQuiz(topicId);
      if (response?.has_next && (response.quiz || response.id)) {
        const quizData = response.quiz || response;
        setNextQuiz(quizData);
        setHasNext(true);
      } else {
        setNextQuiz(null);
        setHasNext(false);
      }
    } catch (err) {
      console.error('Failed to load next quiz:', err);
      setError(err);
      setNextQuiz(null);
      setHasNext(false);
    } finally {
      setLoading(false);
    }
  }, [topicId]);

  useEffect(() => {
    fetchNextQuiz();
  }, [fetchNextQuiz]);

  return {
    nextQuiz,
    hasNext,
    loading,
    error,
    refetch: fetchNextQuiz,
  };
}
